/**
 * Client-side CSV export — the default UI for a small evidentiary report
 * (P19, Roman's R6 undecided): a section on an existing page plus a download,
 * not a dedicated page nobody opens.
 */

import { trackFeature } from '../services/usage';

function escapeCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => unknown;
}

export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const lines = [columns.map(c => escapeCell(c.header)).join(',')];
  for (const row of rows) {
    lines.push(columns.map(c => escapeCell(c.value(row))).join(','));
  }
  return lines.join('\r\n');
}

/** Triggers a browser download of `rows` as a CSV file named `filename`. */
export function downloadCsv<T>(filename: string, rows: T[], columns: CsvColumn<T>[]): void {
  const blob = new Blob([toCsv(rows, columns)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  // Usage telemetry: which screen exported, and how many rows — never the rows themselves.
  trackFeature('csv-export', { rows: rows.length });
}
